import type { JSX } from "react";

import dayjs from "dayjs";
import type { Point } from "highcharts";
import { renderToString } from "react-dom/server";

import {
    combineConfigs,
    noLegendConfig,
    tooltipContainerConfig,
    xAxisCrosshair,
    xAxisLabelsConfig,
    changeBellWithoutTooltip,
    serverResponseFormat,
    shortMonthAndShortYear,
} from "@similarweb/pro-logic-components";
import { colorsPalettes, rgba } from "@similarweb/styles";
import { ChartTooltipTotal } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, percentageFilterWithoutNA, percentageSignFilter } from "app/filters/ngFilters";
import type { IPoint } from "app/pages/keyword-analysis/utils/AnnotationBanner";
import { displayAnnotation, getAnnotationDateForGranularity } from "app/pages/keyword-analysis/utils/AnnotationBanner";
import { defaultGraphTitles } from "app/pages/keyword-research/keyword-analysis/common-components/clicks-over-time-graph/Graph";
import { TooltipWrapperStyled } from "app/pages/keyword-research/keyword-analysis/common-components/clicks-over-time-graph/useGraphTooltip";
import { EGraphGranularities, graphGranularityToString } from "app/common/keyword-research/serp-players/consts";
import { getIntervalConfig } from "app/pages/website-analysis/traffic-sources/display-ads/overview/graph/helpers/DisplayAdsGraphDataParsers";
import DurationService, { BasicDurations } from "app/services/DurationService";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { getTooltipHeader } from "app/UtilitiesAndConstants/UtilitiesComponents/chartTooltipHeaderWithMTDSupport";

import type { ITransformedDataPoint, TotalsData } from "./types";
import { getOthersData, OTHERS } from "./utils";

const timeGranularity = EGraphGranularities.MONTHLY;
const getKeywordAnalysisAvailableFromDate = (): string =>
    dayjs
        .utc(
            swSettings.components.KeywordAnalysisV2Organic.resources.KeywordAnalysisAvailableFromDate as string,
            serverResponseFormat,
        )
        .toString();

const getDateForAnnotationDay = (): string => swSettings.current?.resources?.VersionRerurnDate__1d6d228 as string;
const alertMessageForKeywordAnalysisAvailableFromDate = "keyword.analysis.graph.notification.alert.start.date";
const yAxisFormatter = (value: number) => percentageSignFilter()(value / 100, 0) as string;
const xAxisFormatter = (value: string) => dayjs.utc(value).format(shortMonthAndShortYear);

const getPercentageValue = (
    isOthersVisible: boolean,
    totalsValue: number,
    value: number,
    currentPercentage?: number,
) => {
    if (isOthersVisible) {
        return currentPercentage;
    }
    return (value / totalsValue) * 100;
};

const getGraphTooltip = (graphDataItems: ITransformedDataPoint[], totalsData: Record<string, number>) =>
    function (points: Point[], date: number | string, alertMessage = null, annotationBanner?: JSX.Element) {
        if (!graphDataItems?.length) {
            return;
        }

        const firstValueFilter = abbrNumberFilter();
        const secondValueFilter = percentageFilterWithoutNA();
        const isOthersVisible = points.some((point) => point.series.name === OTHERS);
        const totalsCurrentDataPointValue = totalsData[dayjs.utc(date).format(serverResponseFormat)];

        const rows = points.map((legendDataPoint) => {
            const dataItem = graphDataItems.find((item) => item.name === legendDataPoint.series.name);

            return {
                displayName: dataItem.name,
                isOther: dataItem.isOther,
                color: dataItem.color,
                firstValue: legendDataPoint.y,
                secondValue: getPercentageValue(
                    isOthersVisible,
                    totalsCurrentDataPointValue,
                    legendDataPoint.y,
                    legendDataPoint.percentage,
                ),
            };
        });

        // if others is not visible, calculate others data and recalculate percentage of all visible rows
        if (!isOthersVisible) {
            const updatedOthersData = getOthersData(
                totalsData,
                graphDataItems.filter((item) => !item.isOther),
            );
            const othersCurrentDataPointValue = updatedOthersData.find((item) => item[0] === date)?.[1];

            rows.push({
                displayName: OTHERS,
                isOther: true,
                color: colorsPalettes.carbon[200],
                firstValue: othersCurrentDataPointValue,
                secondValue: getPercentageValue(false, totalsCurrentDataPointValue, othersCurrentDataPointValue),
            });
        }

        const finalRows = rows.map((row) => {
            return {
                ...row,
                // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
                firstValue: firstValueFilter(row.firstValue, 10),
                secondValue: (
                    <span style={{ color: colorsPalettes.carbon[300] }}>{secondValueFilter(row.secondValue, 2)}</span>
                ),
            };
        });

        const { to } = DurationService.getDurationData(BasicDurations.LAST_THIRTEEN_MONTHS).raw;

        return (
            <TooltipWrapperStyled width="340px">
                <ChartTooltipTotal
                    title={getTooltipHeader(
                        graphGranularityToString[EGraphGranularities.MONTHLY] as GranularityNames,
                        [{ x: date }],
                        to.valueOf() as any,
                        false,
                        alertMessage,
                    )}
                    columnsSize="54% 23% 23%"
                    rows={finalRows.filter((row) => !row.isOther)}
                    total={finalRows.find((row) => row.isOther)}
                    headers={[
                        translate(defaultGraphTitles.tooltipTitle),
                        translate(`keywordanalysis.organic2.clicks.over.time.tooltip.${SourceTypeMap.Total}.clicks`),
                        translate("keywordanalysis.organic2.clicks.over.time.tooltip.share.of.total"),
                    ]}
                />
                {annotationBanner ? annotationBanner : null}
            </TooltipWrapperStyled>
        );
    };

export const getGraphConfig = (graphDataItems: ITransformedDataPoint[], totalsData: TotalsData) => {
    const tooltipFormatter = getGraphTooltip(graphDataItems, totalsData);
    const dateForAnnotation = getAnnotationDateForGranularity(
        getDateForAnnotationDay(),
        graphGranularityToString[EGraphGranularities[EGraphGranularities[timeGranularity]]],
    );

    // @ts-expect-error FIXME: 'xAxisFormatter' does not exist in type '{ type: any; }
    const config = combineConfigs({ xAxisFormatter }, [
        noLegendConfig,
        xAxisLabelsConfig,
        xAxisCrosshair,
        tooltipContainerConfig,
        getIntervalConfig(graphGranularityToString[timeGranularity]),
        changeBellWithoutTooltip({ changeStartDate: getKeywordAnalysisAvailableFromDate() }),
        changeBellWithoutTooltip({ changeStartDate: dateForAnnotation }),
        {
            tooltip: {
                shared: true,
                outside: true,
                useHTML: true,
                formatter: function () {
                    return renderToString(
                        tooltipFormatter(
                            this.points,
                            this.x,
                            getKeywordAnalysisAvailableFromDate() === String(this.x) &&
                                alertMessageForKeywordAnalysisAvailableFromDate,
                            displayAnnotation(
                                this.points as unknown as IPoint[],
                                translate("keyword.research.improved.algorithms.annotation.text"),
                                getAnnotationDateForGranularity(
                                    getDateForAnnotationDay(),
                                    graphGranularityToString[timeGranularity] as GranularityNames,
                                ),
                            ),
                        ),
                    );
                },
            },
        },
        {
            chart: {
                height: 260,
                spacingTop: 0,
                marginTop: 0,
                marginBottom: 24,
                plotBackgroundColor: "transparent",
                style: {
                    fontFamily: "Roboto",
                },
            },
            plotOptions: {
                area: {
                    marker: {
                        enabled: false,
                    },
                    stacking: "percent",
                    connectNulls: true,
                    fillOpacity: 1,
                    lineWidth: 0.5,
                },
                series: {
                    animation: false,
                },
            },
            yAxis: [
                {
                    labels: {
                        formatter() {
                            return yAxisFormatter(this.value as number);
                        },
                    },
                    title: {
                        text: undefined,
                    },
                    gridLineColor: rgba(colorsPalettes.carbon[100], 0.4),
                    gridLineWidth: 1,
                    lineWidth: 0,
                    allowDecimals: true,
                    startOnTick: false,
                    gridZIndex: 4,
                    showFirstLabel: false,
                    showLastLabel: true,
                    tickAmount: 5,
                    min: 0,
                    max: 100,
                },
            ],
            xAxis: {
                labels: {
                    formatter() {
                        return xAxisFormatter(this.value as string);
                    },
                    style: {
                        textTransform: "capitalize",
                        fontSize: "11px",
                        color: colorsPalettes.carbon[250],
                        fontFamily: "Roboto",
                    },
                },
                minPadding: 0,
                maxPadding: 0,
                type: "datetime",
            },
        },
    ]);
    return config;
};
